import {useSso} from '@github-ui/use-sso'
import {useCallback, useMemo, useState} from 'react'

import {extractSsoOrgsFromRawQuery} from '../utils/extract-sso-orgs'

/**
 * Manages SSO org detection from multiple sources:
 * 1. `useSso()` app payload — orgs the user hasn't SSO'd into
 * 2. Filter validation callback — orgs the backend flags via 401 responses
 * 3. Raw query matching — org:/repo: values matched against known SSO orgs
 *
 * `ssoOrgsFromFilter` is replaced on each validation cycle. A separate
 * `knownSsoOrgs` set accumulates all orgs ever seen as SSO-protected so
 * that query-matching can find them even when validation doesn't flag
 * them (e.g. multi-org queries where the backend only flags one).
 */
export function useSsoOrgDetection(activeSearchQuery: string) {
  const {ssoOrgs} = useSso()
  const orgNames = ssoOrgs.map(o => o['login']).filter(n => n !== undefined)

  // Orgs detected by the most recent filter validation (full replace each cycle).
  const [ssoOrgsFromFilter, setSsoOrgsFromFilter] = useState<string[]>([])

  // Accumulated set of all orgs ever detected as SSO-protected — used only as
  // input to query matching so multi-org queries don't lose detection.
  const [knownSsoOrgs, setKnownSsoOrgs] = useState<Set<string>>(() => new Set())

  const onSsoOrgsFromFilter = useCallback((orgs: string[]) => {
    setSsoOrgsFromFilter(orgs)
    if (orgs.length > 0) {
      setKnownSsoOrgs(prev => {
        const next = new Set(prev)
        for (const org of orgs) next.add(org)
        return next.size === prev.size ? prev : next
      })
    }
  }, [])

  // Match org:/repo: values in the current query against all known SSO orgs.
  // This catches orgs that validation missed on re-validation of combined queries.
  const ssoOrgsFromQuery = useMemo(
    () => extractSsoOrgsFromRawQuery(activeSearchQuery ?? '', [...orgNames, ...knownSsoOrgs]),
    [activeSearchQuery, orgNames, knownSsoOrgs],
  )

  const allSsoOrgs = useMemo(
    () => [...new Set([...orgNames, ...ssoOrgsFromFilter, ...ssoOrgsFromQuery])],
    [orgNames, ssoOrgsFromFilter, ssoOrgsFromQuery],
  )

  // Orgs detected via filter validation OR query matching (excludes payload-only).
  const filterDetectedSsoOrgs = useMemo(
    () => [...new Set([...ssoOrgsFromFilter, ...ssoOrgsFromQuery])],
    [ssoOrgsFromFilter, ssoOrgsFromQuery],
  )

  return {orgNames, allSsoOrgs, filterDetectedSsoOrgs, onSsoOrgsFromFilter}
}
